import React from "react";
import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import ProgressBar from "@/ui/molecules/ProgressBar";
import FileUpload, {
    testIds as testIdsFileInput,
} from "@/ui/organisms/FileUpload";
import type { DocumentType } from "@/ui/organisms/dealTypes";
import type { LayoutBoxCenteredProps } from "@/ui/layouts/LayoutBoxCentered";
import LayoutBoxCentered from "@/ui/layouts/LayoutBoxCentered";
import {
    ExtractionTypeSelect,
    CoaGateScreen,
    useCoaSelection,
    useCoaAutoFire,
} from "@/ui/organisms/ExtractionTypeSelect";

export type ViewFileUploadParams = {
    useState: (params: { projectId: number; databaseId: number }) => {
        status: "idle" | "pending" | "resolved" | "rejected";
        selectedFileName?: string | null;
        step: "upload" | "documentType" | "extract";
        fileId: number | null;
        documentType: Extract<
            DocumentType,
            "operating_statement" | "rent_roll"
        > | null;
        progressPercentage: number | null;
        progressMessage: string | null;
        error: {
            errorMessage: string;
            isNetworkError?: boolean;
            isExtractionNetworkError?: boolean;
        } | null;
        isAiExtractionEnabled: boolean;
        isRentRollUploadEnabled: boolean;
        // Chart-of-accounts state for the OS-upload CoA prompt (OS-3938).
        // Whether this upload is an Operating Statement one (computed once in
        // the composition so query gating and rendering can't disagree).
        isOsUpload: boolean;
        projectCoaId: number | null;
        projectDealName: string | null;
        projectDescription: string | null;
        availableCoAs: { coaId: number; name: string }[];
        availableCoAsStatus: "pending" | "rejected" | "resolved";
        baselineStatus: "pending" | "rejected" | "resolved";
        coaLocked: boolean;
        retryCoaData: () => void;
    };
    useCases: () => {
        resetFileUploadState: () => void;
        onUnmount: () => void;
        onUploadFilesTriggered: (params: {
            files: FileList;
            projectId: number;
            databaseId: number;
        }) => Promise<void>;
        setDocumentType: (
            documentType: Extract<
                DocumentType,
                "operating_statement" | "rent_roll"
            >,
        ) => void;
        createExtractionFromExistingFile: (params: {
            fileId: number;
            projectId: number;
            fileName: string;
            documentType: Extract<
                DocumentType,
                "operating_statement" | "rent_roll"
            >;
            databaseId: number;
            useAiExtraction?: boolean;
        }) => Promise<void>;
        assignCoa: (params: {
            projectId: number;
            databaseId: number;
            coaId: number;
            dealName: string;
            description: string | null | undefined;
        }) => Promise<number | undefined>;
    };
};
export type ViewFileUploadProps = Omit<BoxProps, "children"> & {
    contentAboveBoxWhenUploadIsIdle?: LayoutBoxCenteredProps["contentAboveBox"];
    LayoutBoxCenteredProps?: LayoutBoxCenteredProps;
    projectId: number;
    databaseId: number;
    handleCancelClicked: () => void;
};

export const testIds = {
    ...testIdsFileInput,
    main: "ViewFileUpload",
};
export const createViewFileUpload = ({
    useState,
    useCases,
}: ViewFileUploadParams) => {
    const ViewFileUpload = (props: ViewFileUploadProps) => {
        const {
            sx,
            className,
            contentAboveBoxWhenUploadIsIdle,
            LayoutBoxCenteredProps,
            projectId,
            databaseId,
            handleCancelClicked,
            ...otherProps
        } = props;

        const {
            onUploadFilesTriggered,
            resetFileUploadState,
            onUnmount,
            setDocumentType,
            createExtractionFromExistingFile,
            assignCoa,
        } = useCases();

        const {
            step,
            status,
            selectedFileName,
            documentType,
            fileId: stateFileId,
            error,
            progressPercentage,
            progressMessage,
            isAiExtractionEnabled,
            isRentRollUploadEnabled,
            isOsUpload,
            projectCoaId,
            projectDealName,
            projectDescription,
            availableCoAs,
            availableCoAsStatus,
            baselineStatus,
            coaLocked,
            retryCoaData,
        } = useState({ projectId, databaseId });
        const idle = status === "idle";
        const uploading = status === "pending";
        const success = status === "resolved";

        const { coaState, coaBlocksContinue, assignCoaThenRun } =
            useCoaSelection({
                isOperatingStatement: isOsUpload,
                baselineStatus,
                coaLocked,
                projectCoaId,
                projectDealName,
                projectDescription,
                availableCoAs,
                availableCoAsStatus,
                retryCoaData,
                projectId,
                databaseId,
                assignCoa,
            });
        React.useEffect(() => {
            resetFileUploadState?.();
            return onUnmount;
        }, [resetFileUploadState, onUnmount]);

        const handleFileDrop = React.useCallback(
            (e: React.DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer === null) return;
                const files = e.dataTransfer.files;
                void onUploadFilesTriggered({ files, projectId, databaseId });
            },
            [onUploadFilesTriggered, projectId, databaseId],
        );

        const handleFileInput = React.useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const files = e.target.files;
                if (files === null) return;
                void onUploadFilesTriggered({ files, projectId, databaseId });
            },
            [onUploadFilesTriggered, projectId, databaseId],
        );

        // Assign the chosen CoA first — never upload an OS to an unassigned
        // project. The hook guards re-entrancy and skips the extraction when the
        // assign fails.
        const assignCoaThenExtract = (
            fileId: number,
            fileName: string,
            documentTypeToExtract: Extract<
                DocumentType,
                "operating_statement" | "rent_roll"
            >,
        ) => {
            void assignCoaThenRun(effectiveProjectId => {
                void createExtractionFromExistingFile({
                    projectId: effectiveProjectId,
                    databaseId,
                    fileId,
                    fileName,
                    documentType: documentTypeToExtract,
                });
            });
        };

        // Rent-Roll-disabled flow: no document-type step. The OS extraction
        // fires by itself once the CoA checks settle and nothing needs the
        // user; the gate screen appears only for an error, a required choice,
        // or a failed silent assign.
        const readyToExtract =
            step === "extract" &&
            idle &&
            error === null &&
            stateFileId !== null &&
            selectedFileName != null &&
            selectedFileName.trim() !== "";
        const onAutoFire = React.useCallback(
            (effectiveProjectId: number) => {
                if (stateFileId === null || selectedFileName == null) return;
                void createExtractionFromExistingFile({
                    projectId: effectiveProjectId,
                    databaseId,
                    fileId: stateFileId,
                    fileName: selectedFileName,
                    documentType: "operating_statement",
                });
            },
            [
                createExtractionFromExistingFile,
                databaseId,
                stateFileId,
                selectedFileName,
            ],
        );
        const { coaGateNeeded } = useCoaAutoFire({
            enabled: !isRentRollUploadEnabled && readyToExtract,
            fileId: stateFileId,
            coaState,
            coaBlocksContinue,
            assignCoaThenRun,
            onFire: onAutoFire,
        });

        const handleNext = () => {
            if (
                !documentType ||
                stateFileId === null ||
                selectedFileName == null ||
                selectedFileName.trim() === "" ||
                // Never proceed while the CoA gate is loading, errored, or unpicked.
                coaBlocksContinue
            ) {
                return;
            }
            if (
                documentType === "operating_statement" &&
                coaState.kind === "ready"
            ) {
                assignCoaThenExtract(
                    stateFileId,
                    selectedFileName,
                    documentType,
                );
                return;
            }
            void createExtractionFromExistingFile({
                projectId,
                databaseId,
                fileId: stateFileId,
                fileName: selectedFileName,
                documentType,
            });
        };

        // Rent-Roll-disabled flow: no document-type step, so the CoA gate is its
        // own screen between upload and extraction.
        const handleGateContinue = () => {
            if (
                stateFileId === null ||
                selectedFileName == null ||
                selectedFileName.trim() === "" ||
                coaBlocksContinue
            ) {
                return;
            }
            assignCoaThenExtract(
                stateFileId,
                selectedFileName,
                "operating_statement",
            );
        };

        const uploadingFile = () => {
            return (
                <Box
                    sx={{
                        padding: "80px",
                    }}
                >
                    <Box
                        id="processing-wrapper"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            className="status-header"
                            sx={{
                                fontSize: "2rem",
                                fontWeight: 600,
                                color: "var(--text-color-dark)",
                            }}
                        >
                            The file is being processed...
                        </Box>
                        <Box
                            component={"p"}
                            className="process-subheader"
                            sx={{
                                color: "var(--proda-black-10)",
                                fontWeight: "400 !important",
                                width: "55%",
                                marginTop: "16px",
                                marginBottom: "16px",
                            }}
                        >
                            Do not close the window while the file is being
                            processed.
                        </Box>
                    </Box>

                    <ProgressBar
                        label={selectedFileName}
                        subLabel={progressMessage}
                        progressPercentage={progressPercentage ?? 0}
                    />
                </Box>
            );
        };

        const onLlmReExtract =
            isAiExtractionEnabled &&
            stateFileId !== null &&
            selectedFileName?.toLowerCase().endsWith(".pdf") === true
                ? () => {
                      void createExtractionFromExistingFile({
                          fileId: stateFileId,
                          projectId,
                          fileName: selectedFileName,
                          documentType: "operating_statement",
                          databaseId,
                          useAiExtraction: true,
                      });
                  }
                : undefined;

        return (
            <Box
                data-testid={testIds.main}
                className={clsx([testIds.main, className])}
                sx={{
                    ...sx,
                }}
                {...otherProps}
            >
                <LayoutBoxCentered
                    contentAboveBox={
                        idle ? contentAboveBoxWhenUploadIsIdle : null
                    }
                    sx={{
                        width: "45%",
                        "@media (max-width: 600px)": {
                            width: "85%",
                        },
                        minWidth: "500px",
                        ...LayoutBoxCenteredProps?.sx,
                    }}
                    {...LayoutBoxCenteredProps}
                >
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            boxSizing: "border-box",
                            fontSize: "18px",
                            color: "var(--text-color-dark)",
                        }}
                    >
                        {step === "documentType" ? (
                            <ExtractionTypeSelect
                                selectedFileName={selectedFileName}
                                documentType={documentType}
                                setDocumentType={setDocumentType}
                                handleNext={handleNext}
                                handleCancelClicked={handleCancelClicked}
                                coaState={coaState}
                            />
                        ) : step === "extract" &&
                          idle &&
                          error === null &&
                          stateFileId !== null &&
                          !isRentRollUploadEnabled ? (
                            // Uploaded, extraction not yet started. Normally
                            // the checks settle and useCoaAutoFire starts the
                            // OS extraction on its own (the processing screen
                            // stays up meanwhile); the gate only interposes
                            // when the user must retry or choose a CoA.
                            coaGateNeeded ? (
                                <CoaGateScreen
                                    coaState={coaState}
                                    onContinue={handleGateContinue}
                                    onCancel={handleCancelClicked}
                                />
                            ) : (
                                uploadingFile()
                            )
                        ) : idle || error !== null ? (
                            <FileUpload
                                error={error}
                                handleFileDrop={handleFileDrop}
                                handleFileInput={handleFileInput}
                                onClose={handleCancelClicked}
                                resetFileUploadState={resetFileUploadState}
                                onLlmReExtract={onLlmReExtract}
                            />
                        ) : uploading || success ? (
                            uploadingFile()
                        ) : null}
                    </Box>
                </LayoutBoxCentered>
            </Box>
        );
    };
    return ViewFileUpload;
};
